import { LEVER_SWAP_CONFIG } from './swapConfig';

function shortAddress(address) {
  if (!address) return '—';
  return `${String(address).slice(0, 8)}…${String(address).slice(-6)}`;
}

function fullOrNull(value) {
  return value == null ? null : String(value);
}

function countWarnings(validation) {
  return Array.isArray(validation?.warnings) ? validation.warnings.length : 0;
}

function countErrors(validation) {
  return Array.isArray(validation?.errors) ? validation.errors.length : 0;
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function rawDataLength(hexData) {
  if (!hexData || typeof hexData !== 'string') return 0;
  return Math.max(0, Math.floor((hexData.length - 2) / 2));
}

function toPlain(value, depth = 0) {
  if (depth > 7) return '[MaxDepth]';
  if (value == null) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'function') return `[Function ${value.name || 'anonymous'}]`;
  if (value && typeof value.toString === 'function' && value._isBigNumber) return value.toString();
  if (Array.isArray(value)) return value.map((item) => toPlain(item, depth + 1));
  if (typeof value === 'object') {
    const output = {};
    Object.keys(value).forEach((key) => {
      const lower = key.toLowerCase();
      if (lower.includes('private') || lower.includes('mnemonic') || lower.includes('seed')) {
        output[key] = '[REDACTED]';
      } else {
        output[key] = toPlain(value[key], depth + 1);
      }
    });
    return output;
  }
  return String(value);
}

function safeJson(value) {
  try {
    return JSON.stringify(toPlain(value), null, 2);
  } catch (error) {
    return JSON.stringify({ serializationError: error?.message || String(error) }, null, 2);
  }
}


function createCopyableDebugText(verbose) {
  if (!verbose) return '';
  const lines = [];
  lines.push('================ STARTAX GUARDED EXECUTION REPORT ================');
  lines.push(`timestamp: ${verbose.timestamp}`);
  lines.push(`phase: ${verbose.phase}`);
  lines.push(`mode: ${verbose.mode}`);
  lines.push(`dryRunOnly: ${verbose.dryRunOnly}`);
  lines.push(`wallet: ${verbose.wallet?.address}`);
  lines.push(`expectedChain: ${verbose.wallet?.chainNameExpected} (${verbose.wallet?.chainIdExpected})`);
  lines.push('');
  lines.push('---------------- SWAP CONFIG ----------------');
  lines.push(safeJson(verbose.swapConfig));
  lines.push('');
  lines.push('---------------- AMOUNTS ----------------');
  lines.push(safeJson(verbose.amounts));
  lines.push('');
  lines.push('---------------- TOKENS ----------------');
  lines.push(safeJson(verbose.tokens));
  lines.push('');
  lines.push('---------------- SUSHI RESPONSE / TX PLAN ----------------');
  lines.push(safeJson({ sushi: verbose.sushi, txPreview: verbose.txPreview }));
  lines.push('');
  lines.push('---------------- APPROVALS ----------------');
  lines.push(verbose.approvals?.length ? safeJson(verbose.approvals) : 'No approvals needed for this run.');
  lines.push('');
  if (verbose.quoteVerification) {
    lines.push('');
    lines.push('---------------- SELL POOL QUOTE VERIFICATION ----------------');
    lines.push(safeJson(verbose.quoteVerification));
  }
  lines.push('');
  lines.push('---------------- VALIDATION ----------------');
  lines.push(safeJson(verbose.validation));
  lines.push('');
  lines.push('---------------- RAW OBJECTS ----------------');
  lines.push(safeJson(verbose.raw));
  lines.push('');
  lines.push('STARTAX_DEBUG_JSON:');
  lines.push(safeJson(verbose));
  lines.push('====================================================================');
  return lines.join('\n');
}

function publishDebugToLocalLogger(verbose) {
  if (!verbose || typeof window === 'undefined') return;
  const text = createCopyableDebugText(verbose);
  window.__STARTAX_LAST_DEBUG_REPORT = verbose;
  window.__STARTAX_LAST_DEBUG_TEXT = text;

  try {
    fetch('http://localhost:8787/startax-debug', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ report: verbose, text }),
    }).catch((error) => {
      console.warn('[StarTax local logger] Could not send debug report:', error?.message || error);
    });
  } catch (error) {
    console.warn('[StarTax local logger] Could not publish debug report:', error?.message || error);
  }
}

function buildApprovalsVerbose(plan, allowanceChecks) {
  const approvalsNeeded = Array.isArray(plan?.approvalsNeeded) ? plan.approvalsNeeded : [];
  const checks = Array.isArray(allowanceChecks) ? allowanceChecks : [];

  return checks.map((check, index) => {
    const matchingApproval = approvalsNeeded.find((approval) =>
      String(approval.tokenAddress || '').toLowerCase() === String(check.tokenAddress || '').toLowerCase()
      && String(approval.spender || '').toLowerCase() === String(check.spender || '').toLowerCase()
    );

    return {
      index,
      tokenAddress: fullOrNull(check.tokenAddress),
      spender: fullOrNull(check.spender),
      ownerAddress: fullOrNull(check.ownerAddress),
      currentAllowanceRaw: fullOrNull(check.allowanceRaw),
      requiredAmountRaw: fullOrNull(check.requiredAmountRaw),
      isEnough: Boolean(check.isEnough),
      needsApproval: !check.isEnough,
      approveAmountRaw: fullOrNull(matchingApproval?.approveAmountRaw),
      label: matchingApproval?.label || `${shortAddress(check.tokenAddress)} → ${shortAddress(check.spender)}`,
    };
  });
}

export function createSwapDebugReport(result) {
  if (!result) return null;
  const plan = result.executionPlan || {};
  const approvals = Array.isArray(plan.approvalsNeeded) ? plan.approvalsNeeded : [];

  return {
    title: 'Guarded execution report',
    mode: result.mode,
    wallet: shortAddress(result.walletAddress),
    amount: result.lastSwapAmountDisplay ?? result.amountInDisplay ?? '0',
    swapMultiplier: result.swapMultiplier || LEVER_SWAP_CONFIG.DEFAULT_SWAP_MULTIPLIER || 1,
    txTo: shortAddress(result.txData?.to),
    tokenIn: shortAddress(result.tokenIn),
    tokenOut: shortAddress(result.tokenOut),
    value: plan.txPreview?.valueDisplay || '0',
    calldataBytes: safeNumber(plan.txPreview?.dataBytes || rawDataLength(result.txData?.data)),
    approvalsNeeded: approvals.length,
    validationStatus: result.validation?.ok ? 'valid' : 'invalid',
    warnings: countWarnings(result.validation),
    txHash: result.realExecution?.swapTxHash || null,
    swapStatus: result.realExecution?.status || null,
    errors: countErrors(result.validation),
    dryRunOnly: Boolean(plan.dryRunOnly),
  };
}

export function createVerboseSwapDebugReport(result) {
  if (!result) return null;
  const plan = result.executionPlan || {};
  const validation = result.validation || {};
  const txData = result.txData || {};
  const swapData = result.swapData || {};

  return {
    timestamp: new Date().toISOString(),
    phase: result.phase || '5B_PRE_REAL_EXECUTION',
    mode: result.mode,
    dryRunOnly: Boolean(plan.dryRunOnly),
    wallet: {
      address: fullOrNull(result.walletAddress),
      chainIdExpected: LEVER_SWAP_CONFIG.CHAIN_ID,
      chainNameExpected: LEVER_SWAP_CONFIG.CHAIN_NAME,
    },
    swapConfig: {
      chainId: LEVER_SWAP_CONFIG.CHAIN_ID,
      routerAddress: LEVER_SWAP_CONFIG.ROUTER_ADDRESS,
      tokenTaxAddress: LEVER_SWAP_CONFIG.PR_ADDRESS,
      pairAddress: LEVER_SWAP_CONFIG.PAIR_ADDRESS,
      wethAddress: LEVER_SWAP_CONFIG.WETH_ADDRESS,
      nativeAddress: LEVER_SWAP_CONFIG.NATIVE_ADDRESS,
      permit2Address: LEVER_SWAP_CONFIG.PERMIT2_ADDRESS,
      sushiApiBase: LEVER_SWAP_CONFIG.SUSHI_SWAP_API_BASE,
      slippageFactor: LEVER_SWAP_CONFIG.SLIPPAGE_FACTOR,
      buyFactor: LEVER_SWAP_CONFIG.BUY_FACTOR,
      defaultSwapMultiplier: LEVER_SWAP_CONFIG.DEFAULT_SWAP_MULTIPLIER,
      swapMultipliers: LEVER_SWAP_CONFIG.SWAP_MULTIPLIERS,
      maxQuoteDeviationBps: LEVER_SWAP_CONFIG.MAX_QUOTE_DEVIATION_BPS,
    },
    amounts: {
      baseAmountRaw: fullOrNull(result.baseAmountRaw),
      standardAmountRaw: fullOrNull(result.standardAmountRaw),
      swapMultiplier: result.swapMultiplier || LEVER_SWAP_CONFIG.DEFAULT_SWAP_MULTIPLIER || 1,
      amountInRaw: fullOrNull(result.amountInRaw),
      amountInDisplay: fullOrNull(result.amountInDisplay),
      expectedTaxOutRaw: fullOrNull(result.expectedTaxOutRaw),
      lastSwapAmountDisplay: fullOrNull(result.lastSwapAmountDisplay),
      useNative: Boolean(result.useNative),
    },
    tokens: {
      tokenIn: fullOrNull(result.tokenIn),
      tokenOut: fullOrNull(result.tokenOut),
    },
    sushi: {
      url: fullOrNull(result.sushiUrl),
      status: swapData.status ?? swapData?.code ?? null,
      assumedAmountIn: swapData.assumedAmountIn ?? null,
      assumedAmountOut: swapData.assumedAmountOut ?? null,
      amountIn: swapData.amountIn ?? null,
      amountOut: swapData.amountOut ?? null,
      routeProcessor: swapData.routeProcessor ?? swapData.routeProcessorAddress ?? null,
      spender: swapData.spender ?? swapData.approveTo ?? null,
    },
    txPreview: {
      to: fullOrNull(txData.to),
      from: fullOrNull(txData.from),
      valueRaw: fullOrNull(txData.value || plan.txPreview?.valueRaw),
      valueETH: fullOrNull(plan.txPreview?.valueDisplay),
      dataBytes: safeNumber(plan.txPreview?.dataBytes || rawDataLength(txData.data)),
      gas: fullOrNull(txData.gas || txData.gasLimit),
      gasPrice: fullOrNull(txData.gasPrice),
      maxFeePerGas: fullOrNull(txData.maxFeePerGas),
      maxPriorityFeePerGas: fullOrNull(txData.maxPriorityFeePerGas),
    },
    approvals: buildApprovalsVerbose(plan, result.allowanceChecks),
    quoteVerification: result.quoteVerification ? toPlain(result.quoteVerification) : null,
    validation: {
      ok: Boolean(validation.ok),
      errors: Array.isArray(validation.errors) ? validation.errors : [],
      warnings: Array.isArray(validation.warnings) ? validation.warnings : [],
      allowedTxTarget: validation.allowedTxTarget ?? null,
      allowedSpender: validation.allowedSpender ?? null,
      chainOk: validation.chainOk ?? null,
      tokenInOk: validation.tokenInOk ?? null,
      tokenOutOk: validation.tokenOutOk ?? null,
      valueOk: validation.valueOk ?? null,
      approvalsOk: validation.approvalsOk ?? null,
      quoteOk: validation.quoteOk ?? null,
    },
    raw: {
      executionPlan: toPlain(plan),
      validationResult: toPlain(validation),
      sushiRaw: toPlain(swapData),
      txData: toPlain(txData),
      allowanceChecks: toPlain(result.allowanceChecks || []),
      quoteVerification: toPlain(result.quoteVerification || null),
      realExecution: toPlain(result.realExecution || null),
      executionEvents: toPlain(result.realExecution?.events || []),
      failureContext: result.realExecution?.status === 'reverted' ? {
        amountInRaw: fullOrNull(result.amountInRaw),
        expectedTaxOutRaw: fullOrNull(result.expectedTaxOutRaw),
        assumedAmountOut: swapData.assumedAmountOut ?? null,
        txTo: fullOrNull(txData.to),
        txValue: fullOrNull(txData.value || plan.txPreview?.valueRaw),
        txHash: fullOrNull(result.realExecution?.swapTxHash),
        blockNumber: fullOrNull(result.realExecution?.swapBlockNumber),
        nonce: fullOrNull(result.realExecution?.swapNonce),
      } : null,
    },
  };
}

export function logSwapDebugReport(result) {
  const report = createSwapDebugReport(result);
  const verbose = createVerboseSwapDebugReport(result);
  if (!report) return null;

  console.groupCollapsed(`[LeverSwap Phase 5C] ${report.mode} verbose guarded execution`);
  console.table(report);

  if (verbose) {
    console.log('================ STARTAX GUARDED EXECUTION REPORT ================');
    console.log('timestamp:', verbose.timestamp);
    console.log('phase:', verbose.phase);
    console.log('mode:', verbose.mode);
    console.log('swap multiplier:', verbose.amounts.swapMultiplier);
    console.log('wallet:', verbose.wallet.address);
    console.log('expected chain:', `${verbose.wallet.chainNameExpected} (${verbose.wallet.chainIdExpected})`);

    console.log('---------------- SWAP CONFIG ----------------');
    console.table(verbose.swapConfig);

    console.log('---------------- AMOUNTS ----------------');
    console.table(verbose.amounts);

    console.log('---------------- TOKENS ----------------');
    console.table(verbose.tokens);

    console.log('---------------- SUSHI RESPONSE / TX PLAN ----------------');
    console.table({
      sushiUrl: verbose.sushi.url,
      txTo: verbose.txPreview.to,
      txFrom: verbose.txPreview.from,
      valueRaw: verbose.txPreview.valueRaw,
      valueETH: verbose.txPreview.valueETH,
      dataBytes: verbose.txPreview.dataBytes,
      gas: verbose.txPreview.gas,
      gasPrice: verbose.txPreview.gasPrice,
      maxFeePerGas: verbose.txPreview.maxFeePerGas,
      maxPriorityFeePerGas: verbose.txPreview.maxPriorityFeePerGas,
      routeProcessor: verbose.sushi.routeProcessor,
      spender: verbose.sushi.spender,
      amountIn: verbose.sushi.amountIn,
      amountOut: verbose.sushi.amountOut,
      assumedAmountIn: verbose.sushi.assumedAmountIn,
      assumedAmountOut: verbose.sushi.assumedAmountOut,
    });

    console.log('---------------- APPROVALS ----------------');
    if (verbose.approvals.length > 0) console.table(verbose.approvals);
    else console.log('No approvals needed for this run.');

    if (verbose.quoteVerification) {
      console.log('---------------- SELL POOL QUOTE VERIFICATION ----------------');
      console.table(verbose.quoteVerification);
    }

    console.log('---------------- VALIDATION ----------------');
    console.table(verbose.validation);

    console.log('---------------- RAW OBJECTS ----------------');
    console.log('executionPlan:', verbose.raw.executionPlan);
    console.log('validationResult:', verbose.raw.validationResult);
    console.log('quoteVerification:', verbose.raw.quoteVerification);
    console.log('sushiRaw:', verbose.raw.sushiRaw);
    console.log('txData:', verbose.raw.txData);
    console.log('allowanceChecks:', verbose.raw.allowanceChecks);

    console.log('STARTAX_DEBUG_JSON:');
    console.log(safeJson(verbose));
    console.log('====================================================================');
    publishDebugToLocalLogger(verbose);
  }

  console.log('Full swap result:', toPlain(result));
  console.groupEnd();
  return report;
}
